// P01 Plattform – Mandanten: Liste (Status, Bildschirme, Speicher), Anlegen mit Einladung des ersten Admins,
// Sperren/Entsperren, Support-Zugriff (P02, protokolliert).
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, fetchEtag } from '../api/client';
import { qk, useTenants } from '../api/queries';
import type { Tenant, TenantListItem } from '../api/types';
import { Icon } from '../components/Icon';
import { SupportDialog } from '../components/Shell';
import { useToast } from '../components/toast';
import { Dialog, Empty, Field, formatBytes, PageHead, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { timeZones } from './Locations';
import { InviteLink } from './Users';
import { NewTenantFeatures, TemplateAccessDialog, TenantFeaturesDialog, type FeatureValue } from './platform/TenantFeatures';

const slugify = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);

export function PlatformTenantsPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: tenants, isLoading } = useTenants();
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [create, setCreate] = useState(false);
  const [support, setSupport] = useState<TenantListItem | null>(null);
  const [features, setFeatures] = useState<TenantListItem | null>(null);
  const [access, setAccess] = useState(false);
  const list = (tenants ?? []).filter((x) => !q || `${x.name} ${x.slug}`.toLowerCase().includes(q.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));

  const setStatus = async (tn: TenantListItem, status: 'active' | 'suspended') => {
    if (status === 'suspended' && !window.confirm(t('platform.confirmSuspend', { name: tn.name }))) return;
    try {
      await api.patch(`/tenants/${tn.id}`, { status }, { ifMatch: await fetchEtag(`/tenants/${tn.id}`) });
      await qc.invalidateQueries({ queryKey: qk.tenants });
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <>
      <PageHead title={t('platform.tenants')} sub={t('platform.tenantsSub')}
        actions={<>
          <button className="btn" onClick={() => setAccess(true)}><Icon name="layout" />{t('features.access.button')}</button>
          <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('platform.newTenant')}</button>
        </>} />
      <div className="toolbar"><input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} /></div>
      {isLoading ? <Spinner /> : !list.length ? (
        <div className="card"><Empty icon="building" title={t('platform.noTenants')} /></div>
      ) : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr><th>{t('common.name')}</th><th>{t('platform.slug')}</th><th>{t('users.status')}</th><th>{t('nav.screens')}</th><th>{t('dashboard.storage')}</th><th /></tr></thead>
            <tbody>
              {list.map((tn) => (
                <tr key={tn.id}>
                  <td><b>{tn.name}</b><div className="muted small">{tn.defaultTimezone} · {tn.defaultLocale.toUpperCase()}</div></td>
                  <td className="mono small">{tn.slug}</td>
                  <td><span className={`pill ${tn.status === 'active' ? 'ok' : tn.status === 'suspended' ? 'warn' : 'error'}`}>{t(`platform.st.${tn.status}`)}</span></td>
                  <td>{tn.deviceCount} / {tn.quotas.devices}</td>
                  <td>
                    <div className="small">{formatBytes(tn.storageUsedBytes, locale)} / {formatBytes(tn.quotas.storageBytes, locale)}</div>
                    <div className="bar" style={{ maxWidth: 140 }}><div style={{ width: `${Math.min(100, (tn.storageUsedBytes / Math.max(1, tn.quotas.storageBytes)) * 100)}%` }} /></div>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {tn.status !== 'deleting' && <button className="btn sm" onClick={() => setFeatures(tn)}><Icon name="grid" />{t('features.button')}</button>}
                    {tn.status === 'active' && <button className="btn sm" onClick={() => setSupport(tn)}><Icon name="key" />{t('platform.support')}</button>}
                    {tn.status === 'active' && <button className="btn sm ghost" onClick={() => void setStatus(tn, 'suspended')}>{t('platform.suspend')}</button>}
                    {tn.status === 'suspended' && <button className="btn sm ghost" onClick={() => void setStatus(tn, 'active')}>{t('platform.activate')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {create && <CreateTenantDialog onClose={() => setCreate(false)} />}
      {support && <SupportDialog tenant={support} onClose={() => setSupport(null)} />}
      {features && <TenantFeaturesDialog tenant={features} onClose={() => setFeatures(null)} />}
      {access && <TemplateAccessDialog tenants={tenants ?? []} onClose={() => setAccess(false)} />}
    </>
  );
}

function CreateTenantDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [lang, setLang] = useState<'de' | 'en'>('de');
  const [tz, setTz] = useState('Europe/Zurich');
  const [email, setEmail] = useState('');
  const [devices, setDevices] = useState(10);
  const [storageGb, setStorageGb] = useState(10);
  const [result, setResult] = useState<{ token: string | null; name: string } | null>(null);
  // Branche und Paket: wählt die Vorlagen der Branche und die allgemeinen Funktionen vor
  const [features, setFeatures] = useState<FeatureValue & { industry: string }>({ industry: 'fitness', preset: null, modules: {}, categories: {} });
  const s = slugTouched ? slug : slugify(name);
  const submit = async () => {
    try {
      const res = await api.post<{ tenant: Tenant; invitation: { token: string } | null }>('/tenants', {
        name: name.trim(), slug: s, defaultLocale: lang, defaultTimezone: tz,
        quotas: { devices, storageBytes: storageGb * 1024 ** 3 },
        ...(email.trim() ? { firstAdminEmail: email.trim() } : {}),
        features: { industry: features.industry, preset: features.preset, modules: features.modules, categories: features.categories },
      });
      await qc.invalidateQueries({ queryKey: qk.tenants });
      setResult({ token: res.invitation?.token ?? null, name: res.tenant.name });
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog wide title={t('platform.newTenant')} onClose={onClose} actions={result ? <button className="btn primary" onClick={onClose}>{t('common.close')}</button> : <>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim() || !s} onClick={submit}>{t('common.create')}</button>
    </>}>
      {result ? (
        <>
          <div className="hint" style={{ background: 'var(--ok-soft)', color: 'var(--ok)', border: 0 }}>{t('platform.created', { name: result.name })}</div>
          {result.token && <InviteLink token={result.token} />}
        </>
      ) : (
        <form className="col" onSubmit={(e) => { e.preventDefault(); if (name.trim() && s) void submit(); }}>
          <Field label={t('common.name')}><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Fitness Muster AG" /></Field>
          <Field label={t('platform.slug')} hint={t('platform.slugHint')}><input className="mono" value={s} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }} /></Field>
          <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label={t('platform.language')}>
              <select value={lang} onChange={(e) => setLang(e.target.value as 'de' | 'en')}><option value="de">Deutsch</option><option value="en">English</option></select>
            </Field>
            <Field label={t('locations.timezone')}><select value={tz} onChange={(e) => setTz(e.target.value)}>{timeZones().map((z) => <option key={z}>{z}</option>)}</select></Field>
            <Field label={t('platform.quotaDevices')}><input type="number" min={1} value={devices} onChange={(e) => setDevices(Math.max(1, Number(e.target.value)))} /></Field>
            <Field label={t('platform.quotaStorage')}><input type="number" min={1} value={storageGb} onChange={(e) => setStorageGb(Math.max(1, Number(e.target.value)))} /></Field>
          </div>
          <Field label={t('platform.firstAdmin')} hint={t('platform.firstAdminHint')}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <NewTenantFeatures value={features} onChange={setFeatures} />
        </form>
      )}
    </Dialog>
  );
}
